/**
 * THE GARDEN HELPER ROBOT — the sim side: what it costs, what the player has told it to do, and what it
 * should do next. No art, no Pixi (see `render/pixi/robotArt.ts` for the drawing, which this never imports —
 * sim importing art drags Pixi into every page, see ARCHITECTURE).
 *
 * ── WHAT IT IS ──
 *
 * A one-time purchase at GARDENING LEVEL 28 — the middle of the climb, not the end of it. It used to sit on
 * L50, the level cap, which put a whole subsystem (this module, its art, the Helper tab and the greenhouse
 * ticker's state machine) behind the terminal rung where few players would ever use it. The cap's reward
 * is now the last fantasy seed pair (BALANCE.md §3b). Once bought it hovers in the greenhouse doing the
 * three jobs the player does by hand: PLANTING a chosen seed into an empty pot, WATERING, and HARVESTING
 * when ripe. Each is opted in PER POT, so a player can automate one shelf and keep tending another themselves.
 *
 * ── THE ORDERS ARE PER POT, AND THAT IS THE WHOLE DESIGN ──
 *
 * `RobotOrders` maps a slotKey to what the robot may do there. A pot with no entry is left completely alone,
 * which is the default: buying the robot changes nothing until the player assigns it work. The three jobs
 * are independent flags plus an optional species, because they genuinely are independent — "water this pot
 * but let me harvest it myself" is a reasonable thing to want, and so is "replant this with cosmos forever
 * but never water it".
 *
 * ── THE SEED CHECK, AND WHY IT IS SHAPED LIKE THIS ──
 *
 * `plant` names a species the robot sows whenever the pot is empty. If the player has no seeds of it, the
 * action DOES NOT HAPPEN and nothing is consumed — and it resumes by itself the moment seeds exist again.
 * That requirement is what makes the check a polling problem: there is no event for "a seed became
 * available" (seeds arrive from harvests, shops, rewards and the dev menu), so something has to look.
 *
 * It is deliberately CHEAP rather than clever. `nextJob` is a single pass over the ORDERS — not over every
 * pot in the greenhouse, and not over the seed inventory — and it exits on the first job it finds. The
 * caller steps the robot from the GREENHOUSE SCENE'S TICKER (useGreenhouse), which runs at 60fps but
 * THROTTLES this scan to once every ROBOT_SCAN_MS (2.5s) and only while the robot is idle — so it costs
 * nothing per frame. A player who has automated every pot of a full five-rack house has 60 orders; scanning
 * 60 objects every 2.5s is nothing, and the alternative — subscribing to seed changes — would spread robot
 * logic across every code path that can grant a seed.
 *
 * ── IT WORKS IN SHIFTS ──
 *
 * The robot works only inside a ROBOT_SHIFT_HOURS (4 h) SHIFT that starts when the player activates it, and
 * inside it the robot works whether the greenhouse is on screen, the tab is hidden, or the game is closed.
 * It used to work for exactly as long as the greenhouse scene was open — so a tab left open overnight
 * harvested and replanted forever, and a closed one did nothing at all (reports/petal-economy.html §5, §7).
 *
 *   • THE START IS THE SERVER'S. The client never sets `robotShiftStart` on the server: it sends a request
 *     counter (`robotShiftReq`), and `sanitise` (server/index.js `arbitrateRobotShift`) stamps a new start
 *     with its OWN clock whenever that counter goes up. A refresh re-sends the same counter and changes
 *     nothing; a device clock wound forward changes nothing either. Recharging is just another request — a
 *     fresh 4 h from now, never an extension of the old one.
 *   • LIVE, `useGreenhouse.stepRobot` refuses work once `robotOnShift` is false — it abandons a job mid-air
 *     and flies home the moment the shift ends, tab open or not.
 *   • AWAY, `replayRobotShift` (below) re-runs the same `nextJob` over the part of the absence that falls
 *     inside the shift and applies each job through the greenhouse's own harvestPot / sowSeed, so a replayed
 *     harvest pays exactly what a live one does. The caller clips the window to the away cap (AWAY_CAP_MS)
 *     first, so robot time can never exceed the growth time the greenhouse is credited for.
 */

import type { FlowerId } from "./flowers";

/** Gardening level the robot unlocks at. Mid-game: six levels after the fifth and last shelf (L22), so it
 *  arrives once the player has a full 60-pot house to tend by hand — when automating it is worth most. */
export const ROBOT_UNLOCK_LEVEL = 28;
/** One-time coin cost. A full 60-pot house nets ~370 coins/min of active play (BALANCE.md §1: 10/min/pot
 *  gross, less the ≈0.38× seed), so 20,000 is about an hour of focused tending, or two or three ordinary
 *  sessions — the biggest one-off buy of the mid-game, saved for rather than ground toward. */
export const ROBOT_PRICE = 20000;

/** How long one shift lasts. Matches the greenhouse's away cap (AWAY_CAP_HOURS) on purpose: a shift started
 *  as the player leaves covers exactly the absence the rest of the greenhouse is credited for. Changing it
 *  changes away income directly — re-run scripts/petal-economy-sim.ts (BALANCE.md §3b). */
export const ROBOT_SHIFT_HOURS = 4;
export const ROBOT_SHIFT_MS = ROBOT_SHIFT_HOURS * 3_600_000;

/** Is the robot on shift at `now`? `start` 0/undefined = never activated. */
export function robotOnShift(start: number | undefined, now: number): boolean {
  return !!start && start > 0 && now >= start && now < start + ROBOT_SHIFT_MS;
}

/** The part of [from, to] inside the shift that started at `start`, or null when they do not meet. */
export function robotShiftWindow(start: number | undefined, from: number, to: number): [number, number] | null {
  if (!start || !(start > 0)) return null;
  const a = Math.max(from, start), b = Math.min(to, start + ROBOT_SHIFT_MS);
  return b > a ? [a, b] : null;
}

/** What the robot is allowed to do at ONE pot. Absent from `RobotOrders` = do nothing here. */
export interface PotOrder {
  /** Species to sow whenever the pot is empty, or undefined to never plant here. The robot only sows when
   *  the player actually holds a seed of it — see the module header. */
  plant?: FlowerId;
  /** Keep this pot watered. */
  water?: boolean;
  /** Harvest this pot when it is ripe. */
  harvest?: boolean;
}

/** slotKey ("rack-tier-col") → what to do there. */
export type RobotOrders = Record<string, PotOrder>;

/** Is this order empty (nothing to do)? Used to drop entries rather than persist a row of `false`s. */
function orderIsEmpty(o: PotOrder | undefined): boolean {
  return !o || (!o.plant && !o.water && !o.harvest);
}

/** Write one field of a pot's order, dropping the entry entirely when it ends up empty. Returns a NEW map —
 *  orders live in React state, so this never mutates. */
export function setOrder(orders: RobotOrders, slotKey: string, patch: Partial<PotOrder>): RobotOrders {
  const next = { ...orders };
  const merged = { ...(next[slotKey] ?? {}), ...patch };
  // An explicit `undefined`/`false` clears the field rather than storing it.
  if (!merged.plant) delete merged.plant;
  if (!merged.water) delete merged.water;
  if (!merged.harvest) delete merged.harvest;
  if (orderIsEmpty(merged)) delete next[slotKey];
  else next[slotKey] = merged;
  return next;
}

/** Coerce an untrusted orders map from the save. Unknown shapes are dropped rather than defaulted — an
 *  order nobody authored would make the robot act on a pot the player never assigned. */
export function coerceOrders(raw: unknown): RobotOrders {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: RobotOrders = {};
  for (const [key, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!v || typeof v !== "object" || Array.isArray(v)) continue;
    const o = v as Record<string, unknown>;
    const order: PotOrder = {};
    if (typeof o.plant === "string" && o.plant) order.plant = o.plant as FlowerId;
    if (o.water === true) order.water = true;
    if (o.harvest === true) order.harvest = true;
    if (!orderIsEmpty(order)) out[key] = order;
  }
  return out;
}

// ── WHAT TO DO NEXT ────────────────────────────────────────────────────────────────────────────────────────

/** One thing for the robot to go and do. */
export interface RobotJob {
  kind: "plant" | "water" | "harvest";
  slotKey: string;
  /** For "plant": the species to sow. */
  species?: FlowerId;
}

/**
 * The state of the world `nextJob` needs. Passed in rather than imported so this file stays pure and
 * testable, and so the caller decides what "ripe" and "thirsty" mean (they are already computed in
 * useGreenhouse, and re-deriving them here would be a second source of truth).
 */
export interface RobotWorld {
  /** Is this pot currently empty (nothing planted)? */
  isEmpty: (slotKey: string) => boolean;
  /** Is this pot's plant ready to harvest? False for an empty pot. */
  isRipe: (slotKey: string) => boolean;
  /** Does this pot want water — planted, not already damp/wet? */
  needsWater: (slotKey: string) => boolean;
  /** How many seeds of this species the player holds. */
  seedCount: (id: FlowerId) => number;
}

/**
 * THE ONE JOB the robot should do next, or null if there is nothing to do (→ it flies back to the dock).
 *
 * PRIORITY IS HARVEST → PLANT → WATER, and the order was arrived at by watching it get this wrong.
 *
 * Harvesting is first because a ripe plant is finished: it occupies a pot, grows no further, and frees both
 * the pot and its payout the moment it is taken.
 *
 * PLANTING BEATS WATERING, which is the part that is easy to get backwards. Watering looks more urgent —
 * a thirsty plant grows slower every second — but a watering job REPEATS: the pot dries out again, so
 * there is almost always a watering job available somewhere. Put watering first and it starves planting
 * completely; the machine spends its entire life topping up the pots it already filled and never sows the
 * empty ones. An empty pot, by contrast, is growing nothing at all, and planting it is a one-shot job that
 * stops being available the instant it is done. First-hand: with WATER ahead of PLANT the robot planted
 * exactly ONE of three orders in 35 seconds and watered the same three pots in a loop for the rest.
 *
 * Watering last still gets done — it is simply the job the robot falls back to when nothing is ripe and
 * nothing is empty, which is what "keep the garden topped up" should mean.
 *
 * Returns the FIRST match rather than a list: the robot can only be in one place at a time, so a queue
 * would just go stale between ticks. Re-running this each tick is how it stays correct when the player
 * harvests something by hand mid-flight.
 */
export function nextJob(orders: RobotOrders, w: RobotWorld): RobotJob | null {
  let plantJob: RobotJob | null = null;
  let waterJob: RobotJob | null = null;
  for (const [slotKey, o] of Object.entries(orders)) {
    if (o.harvest && w.isRipe(slotKey)) return { kind: "harvest", slotKey };
    if (!waterJob && o.water && w.needsWater(slotKey)) waterJob = { kind: "water", slotKey };
    // THE SEED GATE: only a job if the player actually holds one. No seeds → silently skipped this tick,
    // and it resumes by itself once some arrive. Nothing is consumed or reserved here.
    if (!plantJob && o.plant && w.isEmpty(slotKey) && w.seedCount(o.plant) > 0) {
      plantJob = { kind: "plant", slotKey, species: o.plant };
    }
  }
  return plantJob ?? waterJob;
}

// ── HOW LONG THINGS TAKE ───────────────────────────────────────────────────────────────────────────────────
// Shared by the live machine (useGreenhouse.stepRobot) and the away replay below, so the two cannot disagree
// about how much a shift gets done.

/** How long it hovers over a pot working, per job kind (ms). Watering is longest — it is the one with a
 *  visible effect (the droplets) and it needs time to actually wet the soil. */
export const ROBOT_WORK_MS: Record<RobotJob["kind"], number> = { water: 14000, harvest: 700, plant: 900 };
/** Per-pot cooldown after a job (ms) — the live robot's livelock guard (useGreenhouse robotCooldownRef),
 *  applied to the replay too, so a replayed shift tops a pot up no more often than a watched one would. */
export const ROBOT_COOLDOWN_MS = 20000;
/** The replay's stand-in for a flight between pots. The live machine flies at 208 px/s across a house a few
 *  hundred px wide; 1.5 s is a typical trip. */
export const ROBOT_REPLAY_TRAVEL_MS = 1500;
/** How far the replay jumps when there is nothing to do. The live robot looks every 2.5 s; a 30 s step
 *  notices a ripe pot at most half a minute late (against 12+ minute grow times) and keeps a 4 h replay to a
 *  few hundred scans. */
export const ROBOT_REPLAY_IDLE_MS = 30000;

// ── THE AWAY REPLAY ─────────────────────────────────────────────────────────────────────────────────────────

/** What the replay carries between calls: when the machine is next free (a job can run past the end of one
 *  window into the next) and its per-pot cooldowns. Owned by the caller, mutated in place. */
export interface RobotReplayState {
  freeAt: number;
  cooldown: Record<string, number>;
}
export function freshReplayState(): RobotReplayState { return { freeAt: 0, cooldown: {} }; }

/** The greenhouse the replay works on — the caller's own functions, so a replayed job is a real one. */
export interface RobotReplayHooks {
  /** The world as of `t` — the same predicates the live robot asks. */
  world: (t: number) => RobotWorld;
  /** Bring the simulation (growth, moisture) up to `t` before a job changes anything there. */
  advanceTo: (t: number) => void;
  /** Do `job` at time `t` (harvestPot / sowSeed / top the pot up). False if it turned out not to apply. */
  apply: (job: RobotJob, t: number) => boolean;
}

/** Jobs a replay actually did, by kind. */
export type RobotReplayResult = Record<RobotJob["kind"], number>;

/**
 * REPLAY THE ROBOT over [from, to] — time it was not being stepped live (the game closed, the tab hidden,
 * another scene on screen) — clipped to the shift that started at `shiftStart`. Time outside the shift does
 * nothing, which is the whole mechanic.
 *
 * It is the live loop with the flying taken out: ask `nextJob` (same orders, same HARVEST → PLANT → WATER
 * priority, same seed gate), apply the job, then spend the job's travel + work time before asking again; with
 * nothing to do, skip ahead ROBOT_REPLAY_IDLE_MS. A job that would not FINISH before the shift ends is not
 * started — the live robot abandons it at the bell, so the replay must not pay for it either.
 *
 * NO DOUBLE COUNTING, by construction rather than bookkeeping: every job acts on the greenhouse's real state
 * through `apply`, so a harvested pot is empty and cannot be harvested again, a sown pot is full, a spent seed
 * is gone. The caller keeps a robot clock and hands each stretch of time in once, so no span replays twice.
 */
export function replayRobotShift(
  orders: RobotOrders, shiftStart: number | undefined, from: number, to: number,
  state: RobotReplayState, hooks: RobotReplayHooks,
): RobotReplayResult {
  const done: RobotReplayResult = { harvest: 0, plant: 0, water: 0 };
  const win = robotShiftWindow(shiftStart, Math.max(from, state.freeAt), to);
  if (!win || !Object.keys(orders).length) return done;
  const shiftEnd = shiftStart! + ROBOT_SHIFT_MS;
  let [t] = win;
  const end = win[1];
  while (t < end) {
    const base = hooks.world(t);
    const ready = (k: string) => t >= (state.cooldown[k] ?? 0);
    const job = nextJob(orders, {
      isEmpty: (k) => ready(k) && base.isEmpty(k),
      isRipe: (k) => ready(k) && base.isRipe(k),
      needsWater: (k) => ready(k) && base.needsWater(k),
      seedCount: base.seedCount,
    });
    // Nothing to do: look again ROBOT_REPLAY_IDLE_MS later — NOT clipped to `end`. The next look is carried in
    // `freeAt` like a busy job, so the machine scans on the same schedule however the caller slices the time
    // (one 4 h replay on load, or a hidden tab's second-by-second ticks) and the two get the same work done.
    if (!job) { t += ROBOT_REPLAY_IDLE_MS; continue; }
    const takes = ROBOT_REPLAY_TRAVEL_MS + ROBOT_WORK_MS[job.kind];
    if (t + takes > shiftEnd) { t = end; break; }          // the bell rings before it would finish
    hooks.advanceTo(t);
    const did = hooks.apply(job, t);
    if (did) done[job.kind]++;
    state.cooldown[job.slotKey] = t + ROBOT_COOLDOWN_MS;
    // A job that turned out to have nothing in it (a pot already full) costs the trip, not the work — the live
    // machine ends a pour the first frame it finds the pot full.
    t += did ? takes : ROBOT_REPLAY_TRAVEL_MS;
  }
  state.freeAt = t;
  return done;
}

/** How many pots currently carry each kind of order — for the console's summary line. */
export function orderCounts(orders: RobotOrders): { plant: number; water: number; harvest: number } {
  let plant = 0, water = 0, harvest = 0;
  for (const o of Object.values(orders)) {
    if (o.plant) plant++;
    if (o.water) water++;
    if (o.harvest) harvest++;
  }
  return { plant, water, harvest };
}
